import { MissingServicesFilters, MissingServiceStatus } from "@/billing/types/missing-services";
import { getEncounterTypeFilterOptions } from "@/billing/utils/encounter-type-filter";

/**
 * Query-string keys for the applied filter. Each key repeats once per selected option, which is how
 * `updateSearchParams` writes any `string[]`.
 */
export const PROVIDER_FILTER_PARAM = "provider";
export const STATUS_FILTER_PARAM = "status";
export const ENCOUNTER_TYPE_FILTER_PARAM = "encounterType";

const MISSING_SERVICE_STATUSES: MissingServiceStatus[] = ["no-services", "services-not-sent"];

/**
 * Checks whether a string is one of the two missing-Services statuses. Both the URL parser and the
 * drawer's By Status section use this, so they agree on which values are valid.
 * @param value an option key or a URL param value
 * @returns true when `value` is a `MissingServiceStatus`
 */
export function isMissingServiceStatus(value: string): value is MissingServiceStatus {
  return (MISSING_SERVICE_STATUSES as string[]).includes(value);
}

// The FHIR R4 `id` charset. Checked here because `buildSignedAndLockedByTagSearchValue` comma-joins
// these ids into a FHIR OR-list, so a comma in a hand-edited URL would add extra search terms.
const FHIR_ID_PATTERN = /^[A-Za-z0-9\-.]{1,64}$/;

/**
 * Reads the applied filter off the URL, dropping any value the drawer could not have produced.
 * @param searchParams the current query string
 * @returns the filter to query with. A field with no usable value comes back as an empty array,
 * which `loadMissingServices` treats as unfiltered.
 */
export function parseMissingServicesFilters(searchParams: URLSearchParams): MissingServicesFilters {
  const knownEncounterTypeKeys = getEncounterTypeFilterOptions().map((option) => option.key);

  return {
    providerIds: dedupe(
      searchParams.getAll(PROVIDER_FILTER_PARAM).filter((id) => FHIR_ID_PATTERN.test(id)),
    ),
    statuses: dedupe(searchParams.getAll(STATUS_FILTER_PARAM).filter(isMissingServiceStatus)),
    encounterTypeKeys: dedupe(
      searchParams
        .getAll(ENCOUNTER_TYPE_FILTER_PARAM)
        .filter((key) => knownEncounterTypeKeys.includes(key)),
    ),
  };
}

/**
 * Drops repeated values, keeping the first occurrence. A key repeated in a hand-edited URL
 * (`provider=a&provider=a`) would otherwise double-count in the badge and repeat the same
 * reference in the FHIR OR-list.
 * @param values the values read off one query-string key
 * @returns the same values in order, without duplicates
 */
function dedupe<T extends string>(values: T[]): T[] {
  return [...new Set(values)];
}

/**
 * Turns an applied filter into query-string values for `updateSearchParams`.
 * @param filters the filter to write
 * @returns one entry per field. An empty array removes that field's key from the URL.
 */
export function buildMissingServicesFilterParams(
  filters: MissingServicesFilters,
): Record<string, string[]> {
  return {
    [PROVIDER_FILTER_PARAM]: filters.providerIds,
    [STATUS_FILTER_PARAM]: filters.statuses,
    [ENCOUNTER_TYPE_FILTER_PARAM]: filters.encounterTypeKeys,
  };
}

/**
 * Counts the selected options across every section, for the badge on the Filter button.
 * @param filters the applied filter
 * @returns how many options are checked in total. 0 when nothing is filtered.
 */
export function countMissingServicesFilters(filters: MissingServicesFilters): number {
  return filters.providerIds.length + filters.statuses.length + filters.encounterTypeKeys.length;
}
